import html2canvas from 'html2canvas';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { compareDesignImages } from 'utils/designComparison';
import { convertImageUrlToBase64 } from 'utils/links';

interface ScoreConfig {
  designScreenshotUrl: string;
  screenshotWidth: number;
  screenshotHeight: number;
}

interface UseDesignScoreResult {
  score: number | null;
  isComparing: boolean;
}

// Wait for iframe to render after code change
const RENDER_DELAY_MS = 1500;

// Use half resolution for real-time comparison (4x fewer pixels = much faster)
const REALTIME_SCALE = 0.5;

const scheduleIdle = (fn: () => void): number => {
  if (typeof (window as any).requestIdleCallback === 'function') {
    return (window as any).requestIdleCallback(fn);
  }
  return setTimeout(fn, 0) as unknown as number;
};

const cancelIdle = (id: number) => {
  if (typeof (window as any).cancelIdleCallback === 'function') {
    (window as any).cancelIdleCallback(id);
  } else {
    clearTimeout(id);
  }
};

const useDesignScore = (
  codeFrameUrl: string,
  scoreConfig: ScoreConfig | null | undefined,
  containerRef: RefObject<HTMLDivElement | null>
): UseDesignScoreResult => {
  const [score, setScore] = useState<number | null>(null);
  const [isComparing, setIsComparing] = useState(false);

  const isComparingRef = useRef(false);
  const isVisibleRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleRef = useRef<number | null>(null);
  const designBase64Ref = useRef<string | null>(null);
  const designUrlRef = useRef<string | null>(null);
  const pendingCompareRef = useRef(false);
  const latestCodeFrameUrlRef = useRef(codeFrameUrl);

  latestCodeFrameUrlRef.current = codeFrameUrl;

  const captureAndCompare = useCallback(async () => {
    if (!scoreConfig || !designBase64Ref.current) return;
    if (isComparingRef.current) return;

    const currentUrl = latestCodeFrameUrlRef.current;
    if (!currentUrl) return;

    // If not visible, mark as pending and skip
    if (!isVisibleRef.current) {
      pendingCompareRef.current = true;
      return;
    }

    isComparingRef.current = true;
    setIsComparing(true);

    try {
      const container = containerRef.current;
      const iframe = (container?.querySelector('.bds-code-frame') || document.querySelector('.bds-code-frame')) as HTMLIFrameElement;

      if (!iframe) {
        isComparingRef.current = false;
        setIsComparing(false);
        return;
      }

      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;

      if (!iframeDocument?.body) {
        isComparingRef.current = false;
        setIsComparing(false);
        return;
      }

      // Use scaled-down resolution for faster real-time comparison
      const canvas = await html2canvas(iframeDocument.body, {
        useCORS: true,
        allowTaint: true,
        width: Math.round(scoreConfig.screenshotWidth * REALTIME_SCALE),
        height: Math.round(scoreConfig.screenshotHeight * REALTIME_SCALE),
      });

      const userScreenshot = canvas.toDataURL('image/png');

      // Schedule the CPU-intensive comparison during idle time
      idleRef.current = scheduleIdle(() => {
        compareDesignImages(userScreenshot, designBase64Ref.current!)
          .then((result) => {
            setScore(result);
          })
          .catch(() => {
            // Silently handle
          })
          .finally(() => {
            isComparingRef.current = false;
            setIsComparing(false);
          });
      });
    } catch {
      // Silently handle errors — don't trigger CRA error overlay
      isComparingRef.current = false;
      setIsComparing(false);
    }
  }, [scoreConfig]);

  // Track visibility via IntersectionObserver
  useEffect(() => {
    const el = containerRef.current;

    if (!el || !scoreConfig) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const wasHidden = !isVisibleRef.current;
        isVisibleRef.current = entry.isIntersecting;

        // If we just became visible and have a pending comparison, run it
        if (entry.isIntersecting && wasHidden && pendingCompareRef.current) {
          pendingCompareRef.current = false;
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => {
            captureAndCompare();
          }, RENDER_DELAY_MS);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [containerRef, scoreConfig, captureAndCompare]);

  // Cache the design screenshot base64 conversion (once)
  useEffect(() => {
    if (!scoreConfig?.designScreenshotUrl) return;

    if (designUrlRef.current === scoreConfig.designScreenshotUrl) return;

    designUrlRef.current = scoreConfig.designScreenshotUrl;

    convertImageUrlToBase64(scoreConfig.designScreenshotUrl).then((base64) => {
      designBase64Ref.current = base64;
    });
  }, [scoreConfig?.designScreenshotUrl]);

  // Run comparison when codeFrameUrl changes (only if visible)
  useEffect(() => {
    if (!scoreConfig || !codeFrameUrl) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If not visible, just mark pending
    if (!isVisibleRef.current) {
      pendingCompareRef.current = true;
      return;
    }

    timeoutRef.current = setTimeout(() => {
      captureAndCompare();
    }, RENDER_DELAY_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [codeFrameUrl, scoreConfig, captureAndCompare]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (idleRef.current) cancelIdle(idleRef.current);
    };
  }, []);

  return { score, isComparing };
};

export default useDesignScore;
